// Fiche produit de l'Admin : conversions et calculs PURS (aucun React). L'admin raisonne en EUROS ; la conversion
// euros <-> centimes ne se fait qu'ici. Repris de l'ancien ecran « Fournisseurs et produits d'impression », avec ses regles.

export const centsToEuros = (cents) => (cents === null || cents === undefined ? '' : (Number(cents) / 100).toFixed(2));

/** « 12,5 » -> 1250 ; vide -> null ; illisible -> NaN (le serveur le refuse). */
export const eurosToCents = (euros) => {
  const texte = String(euros ?? '').trim().replace(',', '.');
  if (texte === '') return null;
  const nombre = Number(texte);
  return Number.isFinite(nombre) ? Math.round(nombre * 100) : NaN;
};

export const formatEuros = (cents) => (cents === null || cents === undefined || Number.isNaN(cents) ? '—' : `${centsToEuros(cents).replace('.', ',')} €`);

/** Plus petit prix >= `brutCents` qui se termine par `terminaison` centimes (90 -> ... 49,90 ; 59,90). */
export function arrondirAvecTerminaison(brutCents, terminaison) {
  const fin = Number.isInteger(terminaison) ? terminaison : 90;
  const euros = Math.ceil((brutCents - fin) / 100);
  return Math.max(0, euros) * 100 + fin;
}

/** TVA payee aux fournisseurs (Prodigi, Gelato) : les couts saisis dans l'Admin sont HT, mais Bookipix ne recupere pas la
 *  TVA (franchise, 2026-10-23) — le VRAI cout est donc TTC. Oubli constate sur la premiere commande Prestige : prix fixes a
 *  cout HT + 20 %, la TVA de 20 % mangeait toute la marge (commande vendue 51,86 €, payee 53,03 €).
 *  Le jour ou Bookipix recupere la TVA, le calcul reste JUSTE : il faudra alors facturer la TVA au client, et
 *  cout HT x (1 + marge) x 1,20 donne le meme prix TTC. */
export const TVA_FOURNISSEUR_PCT = 20;

/** Cout reel (TTC) d'un cout fournisseur saisi HT. */
export const coutReel = (coutHtCents) => (
  coutHtCents === null || coutHtCents === undefined || !Number.isFinite(coutHtCents) ? coutHtCents : Math.round(coutHtCents * (1 + TVA_FOURNISSEUR_PCT / 100))
);

/** Port conseille : le cout reel TTC, arrondi aux 10 centimes superieurs (port refacture a prix coutant, decision 2026-10-16). */
export const portConseille = (coutHtCents) => (
  coutHtCents === null || coutHtCents === undefined || !Number.isFinite(coutHtCents) ? null : Math.ceil(coutReel(coutHtCents) / 10) * 10
);

/** Marge en % du COUT ; null si l'un des deux manque. */
export const margePourcent = (prixCents, coutCents) => (
  Number.isFinite(prixCents) && Number.isFinite(coutCents) && prixCents !== null && coutCents ? Math.round(((prixCents - coutCents) / coutCents) * 100) : null
);

/** Prix conseille pour une marge visee : base arrondie a la terminaison, « par 2 pages » arrondi a l'inferieur... au centime superieur. */
export function prixConseille({ coutBaseCents, coutPar2Cents, marge, terminaison = 90 }) {
  if (!Number.isFinite(marge) || marge <= 0) return { base: null, par2: null };
  return {
    // marge appliquee au cout REEL (TTC, voir TVA_FOURNISSEUR_PCT), jamais au cout HT
    base: Number.isFinite(coutBaseCents) && coutBaseCents !== null ? arrondirAvecTerminaison(coutReel(coutBaseCents) * (1 + marge / 100), terminaison) : null,
    par2: Number.isFinite(coutPar2Cents) && coutPar2Cents !== null ? Math.ceil(coutReel(coutPar2Cents) * (1 + marge / 100)) : null
  };
}

export const FICHE_VIDE = {
  providerId: '', sku: '', nameFr: '', nameEn: '', taglineFr: '', taglineEn: '', recommended: false, sortOrder: '100',
  coverType: 'hard', binding: '', orientation: 'portrait', styleProfile: 'standard', paperFr: '', paperEn: '',
  trimWidthMm: '', trimHeightMm: '', safeMarginMm: '10', minPages: '24', maxPages: '200', pageStep: '2', basePages: '24',
  costBase: '', costPer2Pages: '', marginPercent: '', roundingEnding: '90', priceBase: '', pricePer2Pages: '',
  freeShippingThreshold: '', shipping: [],
  // 'off' (inactif) | 'testers' (reserve aux administrateurs et testeurs) | 'public'
  visibility: 'off'
};

export const versBrouillon = (produit) => ({
  providerId: produit.providerId,
  sku: produit.sku,
  nameFr: produit.name.fr,
  nameEn: produit.name.en,
  taglineFr: produit.tagline.fr,
  taglineEn: produit.tagline.en,
  recommended: produit.recommended,
  sortOrder: String(produit.sortOrder),
  coverType: produit.coverType,
  binding: produit.binding,
  orientation: produit.orientation,
  styleProfile: produit.styleProfile || 'standard',
  paperFr: (produit.paper && produit.paper.fr) || '',
  paperEn: (produit.paper && produit.paper.en) || '',
  trimWidthMm: String(produit.trimWidthMm),
  trimHeightMm: String(produit.trimHeightMm),
  safeMarginMm: String(produit.safeMarginMm),
  minPages: String(produit.minPages),
  maxPages: String(produit.maxPages),
  pageStep: String(produit.pageStep),
  basePages: String(produit.basePages),
  costBase: centsToEuros(produit.costBaseCents),
  costPer2Pages: centsToEuros(produit.costPer2PagesCents),
  marginPercent: produit.marginPercent === null || produit.marginPercent === undefined ? '' : String(produit.marginPercent),
  roundingEnding: produit.roundingEndingCents === null || produit.roundingEndingCents === undefined ? '' : String(produit.roundingEndingCents),
  priceBase: centsToEuros(produit.priceBaseCents),
  pricePer2Pages: centsToEuros(produit.pricePer2PagesCents),
  freeShippingThreshold: centsToEuros(produit.freeShippingThresholdCents),
  shipping: Object.entries(produit.shipping || {}).map(([pays, ligne]) => ({
    country: pays,
    price: centsToEuros(ligne.priceCents),
    cost: centsToEuros(ligne.costCents)
  })),
  visibility: produit.visibility || (produit.active ? 'testers' : 'off')
});

export const versPayload = (brouillon) => {
  const entier = (valeur) => (String(valeur).trim() === '' ? null : Number(valeur));
  const decimal = (valeur) => Number(String(valeur).replace(',', '.'));
  const shipping = {};
  for (const ligne of brouillon.shipping) {
    const pays = String(ligne.country || '').trim().toUpperCase();
    if (!pays) continue;
    shipping[pays] = { priceCents: eurosToCents(ligne.price), costCents: eurosToCents(ligne.cost) };
  }
  return {
    providerId: brouillon.providerId,
    sku: brouillon.sku,
    name: { fr: brouillon.nameFr, en: brouillon.nameEn },
    tagline: { fr: brouillon.taglineFr, en: brouillon.taglineEn },
    recommended: brouillon.recommended,
    sortOrder: entier(brouillon.sortOrder),
    coverType: brouillon.coverType,
    binding: brouillon.binding,
    orientation: brouillon.orientation,
    styleProfile: brouillon.styleProfile,
    paper: { fr: brouillon.paperFr, en: brouillon.paperEn },
    trimWidthMm: decimal(brouillon.trimWidthMm),
    trimHeightMm: decimal(brouillon.trimHeightMm),
    safeMarginMm: decimal(brouillon.safeMarginMm),
    minPages: entier(brouillon.minPages),
    maxPages: entier(brouillon.maxPages),
    pageStep: entier(brouillon.pageStep),
    basePages: entier(brouillon.basePages),
    costBaseCents: eurosToCents(brouillon.costBase),
    costPer2PagesCents: eurosToCents(brouillon.costPer2Pages),
    marginPercent: String(brouillon.marginPercent).trim() === '' ? null : Number(brouillon.marginPercent),
    roundingEndingCents: entier(brouillon.roundingEnding),
    priceBaseCents: eurosToCents(brouillon.priceBase),
    pricePer2PagesCents: eurosToCents(brouillon.pricePer2Pages),
    freeShippingThresholdCents: eurosToCents(brouillon.freeShippingThreshold),
    shipping,
    active: brouillon.visibility !== 'off',
    // Reserve aux testeurs sauf choix explicite « Public ».
    testersOnly: brouillon.visibility !== 'public'
  };
};

/** Corps de PUT /api/admin/pricing pour un format HISTORIQUE (Gelato) : seuls base et « par 2 pages » changent ; le reste est conserve cote serveur. */
export const versTarifHistorique = (formatId, brouillon) => ({
  formats: { [formatId]: { basePriceCents: eurosToCents(brouillon.priceBase), pricePer2PagesCents: eurosToCents(brouillon.pricePer2Pages) } }
});

/** Etat d'un produit pour le tableau : 'off' | 'testers' | 'public' (les formats historiques sont toujours en vente). */
export const etatProduit = (produit) => (produit.builtin ? 'public' : (produit.visibility || (produit.active ? 'testers' : 'off')));

/** Le brouillon differe-t-il de la fiche enregistree ? (confirmation avant de fermer un panneau modifie) */
export const brouillonModifie = (produit, brouillon) => {
  const base = produit ? versBrouillon(produit) : FICHE_VIDE;
  return JSON.stringify(base) !== JSON.stringify(brouillon);
};
